import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import {
  Wallet,
  CircleCheck,
  Clock,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  XCircle,
  Search,
  Copy,
  Check,
  CircleAlert,
} from 'lucide-react';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const ROWS_PER_PAGE = 10;
const SLIDE_INTERVAL = 4000; // 4s auto-slide

const AdminWithdraws = () => {
  const [data, setData] = useState({ stats: null, requests: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // ==================== FETCH ====================
  const fetchWithdraws = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.get(`${baseUrl}/api/admin/withdraws`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data?.status) {
        setData({
          stats: response.data.data.stats || {},
          requests: response.data.data.requests || [],
        });
      } else {
        toast.error(response.data?.msg || 'Failed to load withdrawals');
      }
    } catch (error) {
      console.error('Error fetching withdraws:', error);
      toast.error(error.response?.data?.msg || 'Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWithdraws();
  }, [fetchWithdraws]);

  // ==================== CAROUSEL ====================
  const slides = useMemo(() => {
    const s = data.stats || {};
    return [
      {
        key: 'today-total',
        label: 'Today Total',
        amount: s.todayAmt || 0,
        count: s.todayNum || 0,
        color: 'blue',
        icon: <Wallet className="w-6 h-6" />,
      },
      {
        key: 'today-paid',
        label: 'Today Paid',
        amount: s.todayPaidAmt || 0,
        count: s.todayPaidNum || 0,
        color: 'emerald',
        icon: <CircleCheck className="w-6 h-6" />,
      },
      {
        key: 'yest-total',
        label: 'Yesterday Total',
        amount: s.yestAmt || 0,
        count: s.yestNum || 0,
        color: 'amber',
        icon: <Clock className="w-6 h-6" />,
      },
      {
        key: 'yest-paid',
        label: 'Yesterday Paid',
        amount: s.yestPaidAmt || 0,
        count: s.yestPaidNum || 0,
        color: 'emerald',
        icon: <CircleCheck className="w-6 h-6" />,
      },
      {
        key: 'mtd-total',
        label: 'MTD Total',
        amount: s.mtdAmt || 0,
        count: s.mtdNum || 0,
        color: 'indigo',
        icon: <Calendar className="w-6 h-6" />,
      },
      {
        key: 'mtd-paid',
        label: 'MTD Paid',
        amount: s.mtdPaidAmt || 0,
        count: s.mtdPaidNum || 0,
        color: 'emerald',
        icon: <CircleCheck className="w-6 h-6" />,
      },
    ];
  }, [data.stats]);

  // Auto-slide effect
  useEffect(() => {
    if (isPaused || slides.length === 0 || loading) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, slides.length, loading]);

  const goToSlide = (idx) => {
    setCurrentSlide(idx);
  };
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);

  // ==================== FILTERED REQUESTS ====================
  const filteredRequests = useMemo(() => {
    let list = data.requests || [];

    // Tab filter (pending / approved / rejected)
    list = list.filter(
      (r) => String(r.status || '').toLowerCase() === activeTab
    );

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          String(r.txn_id).includes(q) ||
          (r.name || '').toLowerCase().includes(q) ||
          (r.email || '').toLowerCase().includes(q) ||
          (r.number || '').toLowerCase().includes(q) ||
          (r.payout_address || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [data.requests, activeTab, searchQuery]);

  // Reset page on tab/search change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [activeTab, searchQuery]);

  // ==================== PAGINATION ====================
  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / ROWS_PER_PAGE));
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredRequests.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRequests, currentPage]);

  // ==================== SELECTION ====================
  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedRequests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedRequests.map((r) => r.txn_id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ==================== ACTIONS ====================
  const handleBulkAction = async (type) => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one request');
      return;
    }
    setActionLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.post(
        `${baseUrl}/api/admin/withdraws/action`,
        { ids: selectedIds, type },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data?.status) {
        toast.success(response.data.msg || `Requests ${type}d successfully`);
        setSelectedIds([]);
        fetchWithdraws();
      } else {
        toast.error(response.data?.msg || `Failed to ${type}`);
      }
    } catch (error) {
      console.error(`Error on ${type}:`, error);
      toast.error(error.response?.data?.msg || `Failed to ${type}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSingleAction = async (id, type) => {
    setActionLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.post(
        `${baseUrl}/api/admin/withdraws/action`,
        { ids: [id], type },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data?.status) {
        toast.success(response.data.msg || `Request ${type}d successfully`);
        fetchWithdraws();
      } else {
        toast.error(response.data?.msg || `Failed to ${type}`);
      }
    } catch (error) {
      console.error(`Error on ${type}:`, error);
      toast.error(error.response?.data?.msg || `Failed to ${type}`);
    } finally {
      setActionLoading(false);
    }
  };

  // ==================== COPY ====================
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // ==================== COLOR HELPERS ====================
  const colorClasses = (color) => {
    switch (color) {
      case 'blue':
        return {
          bg: 'bg-blue-500/10',
          text: 'text-blue-400',
          border: 'border-blue-500/20',
          glow: 'bg-blue-500',
        };
      case 'emerald':
        return {
          bg: 'bg-emerald-500/10',
          text: 'text-emerald-400',
          border: 'border-emerald-500/20',
          glow: 'bg-emerald-500',
        };
      case 'amber':
        return {
          bg: 'bg-amber-500/10',
          text: 'text-amber-400',
          border: 'border-amber-500/20',
          glow: 'bg-amber-500',
        };
      case 'indigo':
        return {
          bg: 'bg-indigo-500/10',
          text: 'text-indigo-400',
          border: 'border-indigo-500/20',
          glow: 'bg-indigo-500',
        };
      default:
        return {
          bg: 'bg-zinc-500/10',
          text: 'text-zinc-400',
          border: 'border-zinc-500/20',
          glow: 'bg-zinc-500',
        };
    }
  };

  const currentSlideData = slides[currentSlide];
  const currentColors = currentSlideData ? colorClasses(currentSlideData.color) : colorClasses('blue');

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes slideFade {
          0% { opacity: 0; transform: translateX(20px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-fade { animation: slideFade 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }
        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progressBar ${SLIDE_INTERVAL}ms linear forwards;
        }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }

        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="p-4 sm:p-8 max-w-[1600px] mx-auto space-y-8">

        {/* ==================== HEADER ==================== */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 animate-spring-in">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-zinc-100">
              Withdrawal Management
            </h2>
            <p className="text-zinc-500 text-sm font-medium mt-1">
              Review and process publisher payment requests
            </p>
          </div>

          {/* Tabs — now horizontally scrollable on mobile */}
          <div className="w-full md:w-fit overflow-x-auto no-scrollbar -mx-1 px-1">
          {/* Tabs */}
          <div className="flex bg-zinc-900/90 p-1 rounded-2xl w-fit border border-zinc-800">
            {TABS.map((tab) => {
              const count = (data.requests || []).filter(
                (r) => String(r.status || '').toLowerCase() === tab.key
              ).length;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-wider sm:tracking-widest transition-all flex items-center gap-1.5 ${
                    activeTab === tab.key
                      ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`px-1.5 py-0.5 rounded-md text-[9px] font-black ${
                      activeTab === tab.key
                        ? 'bg-zinc-950/20 text-zinc-950'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          </div>
        </div>

        {/* ==================== UNIQUE STATS CAROUSEL ==================== */}
        <div
          className="relative animate-spring-in"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Main Large Slide */}
            <div className="lg:col-span-2 relative bg-zinc-900/90 p-6 sm:p-8 rounded-[2.5rem] border border-zinc-800 shadow-xl overflow-hidden min-h-[180px] group">
              {/* Ambient glow */}
              <div
                className={`absolute top-0 right-0 w-40 h-40 -mr-10 -mt-10 rounded-full blur-3xl opacity-30 transition-all duration-700 group-hover:opacity-50 group-hover:scale-125 ${currentColors.glow}`}
              />

              {loading ? (
                <div className="space-y-4">
                  <div className="w-32 h-4 bg-zinc-800 rounded shimmer-bg" />
                  <div className="w-48 h-12 bg-zinc-800 rounded-lg shimmer-bg" />
                  <div className="w-24 h-4 bg-zinc-800 rounded shimmer-bg" />
                </div>
              ) : (
                <>
                  <div className="relative z-10 flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${currentColors.bg} ${currentColors.text} border ${currentColors.border} transition-transform duration-300 group-hover:scale-110`}
                      >
                        {currentSlideData.icon}
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                          {currentSlideData.label}
                        </p>
                        <p className="text-xs font-bold text-zinc-400 mt-0.5">
                          Current period snapshot
                        </p>
                      </div>
                    </div>

                    {/* Slide counter */}
                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                      {String(currentSlide + 1).padStart(2, '0')} /{' '}
                      {String(slides.length).padStart(2, '0')}
                    </span>
                  </div>

                  <div
                    key={currentSlide}
                    className="relative z-10 flex items-baseline gap-3 animate-slide-fade"
                  >
                    <h3 className={`text-4xl sm:text-5xl font-black tracking-tight ${currentColors.text}`}>
                      ₹{Number(currentSlideData.amount || 0).toLocaleString()}
                    </h3>
                    <span className="text-sm font-bold text-zinc-500">
                      ({currentSlideData.count}{' '}
                      {currentSlideData.count === 1 ? 'Request' : 'Requests'})
                    </span>
                  </div>

                  {/* Progress bar */}
                  {!isPaused && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800 overflow-hidden">
                      <div
                        key={currentSlide}
                        className={`h-full ${currentColors.glow} animate-progress`}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Manual navigation buttons */}
              <button
                onClick={prevSlide}
                aria-label="Previous stat"
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-zinc-950/80 border border-zinc-800 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 rounded-xl opacity-0 group-hover:opacity-100 transition-all micro-interaction"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={nextSlide}
                aria-label="Next stat"
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-zinc-950/80 border border-zinc-800 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 rounded-xl opacity-0 group-hover:opacity-100 transition-all micro-interaction"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right Side Stack — compact list + dots */}
            <div className="bg-zinc-900/90 p-6 rounded-[2.5rem] border border-zinc-800 shadow-xl flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    All Stats
                  </p>
                  <button
                    onClick={() => setIsPaused((p) => !p)}
                    className="text-[9px] font-black uppercase tracking-widest text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    {isPaused ? '▶ Resume' : '❚❚ Pause'}
                  </button>
                </div>

                <div className="space-y-1.5">
                  {slides.map((s, idx) => {
                    const c = colorClasses(s.color);
                    const isActive = idx === currentSlide;
                    return (
                      <button
                        key={s.key}
                        onClick={() => goToSlide(idx)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all text-left ${
                          isActive
                            ? 'bg-zinc-950 border border-zinc-700'
                            : 'hover:bg-zinc-950/60 border border-transparent'
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full shrink-0 transition-all ${
                            isActive ? c.glow : 'bg-zinc-700'
                          }`}
                        />
                        <span
                          className={`text-[11px] font-bold flex-1 truncate transition-colors ${
                            isActive ? 'text-zinc-100' : 'text-zinc-500'
                          }`}
                        >
                          {s.label}
                        </span>
                        <span
                          className={`text-[10px] font-black ${
                            isActive ? c.text : 'text-zinc-600'
                          }`}
                        >
                          ₹{Number(s.amount || 0).toLocaleString()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dot indicators */}
              <div className="flex justify-center gap-1.5 mt-4 pt-4 border-t border-zinc-800">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx === currentSlide
                        ? 'w-6 bg-emerald-400'
                        : 'w-1.5 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ==================== SEARCH + BULK ACTIONS ==================== */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 animate-spring-in">

          <div className="relative w-full lg:w-96">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              placeholder="Search by TXN, name, email, UPI..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-900/90 border border-zinc-800 rounded-2xl focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 font-bold text-sm shadow-sm text-zinc-100 placeholder:text-zinc-600"
            />
          </div>

          {activeTab === 'pending' && selectedIds.length > 0 && (
            <div className="flex items-center gap-3 animate-spring-in">
              <span className="text-xs font-bold text-zinc-400">
                {selectedIds.length} selected
              </span>
              <button
                onClick={() => handleBulkAction('reject')}
                disabled={actionLoading}
                className="px-5 py-3 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all flex items-center gap-2 micro-interaction disabled:opacity-50"
              >
                <XCircle className="w-3.5 h-3.5" />
                Reject Selected
              </button>
              <button
                onClick={() => handleBulkAction('approve')}
                disabled={actionLoading}
                className="px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 micro-interaction shadow-lg shadow-emerald-500/20 disabled:opacity-50"
              >
                {actionLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                )}
                Approve Selected
              </button>
            </div>
          )}
        </div>

        {/* ==================== REQUESTS TABLE ==================== */}
        <div className="space-y-4 animate-spring-in">
          <div className="flex items-center gap-2">
            <div
              className={`h-2 w-2 rounded-full ${
                activeTab === 'pending'
                  ? 'bg-amber-500 animate-pulse'
                  : activeTab === 'approved'
                  ? 'bg-emerald-500'
                  : 'bg-rose-500'
              }`}
            />
            <h3 className="text-lg font-black text-zinc-100 capitalize">
              {activeTab} Requests
            </h3>
          </div>

          <div className="bg-zinc-900/90 border border-zinc-800 rounded-[2.5rem] shadow-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto custom-scrollbar">
              <table className="w-full text-left border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10 bg-zinc-900">
                  <tr className="border-b border-zinc-800 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    {activeTab === 'pending' && (
                      <th className="px-6 py-5 w-12">
                        <input
                          type="checkbox"
                          checked={
                            paginatedRequests.length > 0 &&
                            selectedIds.length === paginatedRequests.length
                          }
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-emerald-500 focus:ring-emerald-500/30 cursor-pointer"
                        />
                      </th>
                    )}
                    <th className="px-6 py-5">TXN ID / Date</th>
                    <th className="px-6 py-5">User Details</th>
                    <th className="px-6 py-5">Payout Address</th>
                    <th className="px-6 py-5 text-right">Amount</th>
                    <th className="px-6 py-5 text-center">Status</th>
                    {activeTab === 'pending' && (
                      <th className="px-6 py-5 text-right">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(activeTab === 'pending' ? 7 : 6)].map((__, j) => (
                          <td key={j} className="px-6 py-5">
                            <div className="h-4 bg-zinc-800 rounded shimmer-bg" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : paginatedRequests.length === 0 ? (
                    <tr>
                      <td
                        colSpan={activeTab === 'pending' ? 7 : 6}
                        className="px-6 py-20 text-center"
                      >
                        <div className="flex flex-col items-center">
                          <CircleAlert className="w-10 h-10 text-zinc-700 mb-3" />
                          <p className="text-sm font-bold text-zinc-500">
                            No withdrawal requests found in this category.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedRequests.map((row) => {
                      const isSelected = selectedIds.includes(row.txn_id);
                      return (
                        <tr
                          key={row.txn_id}
                          className={`hover:bg-emerald-500/[0.03] transition-colors group ${
                            isSelected ? 'bg-emerald-500/[0.05]' : ''
                          }`}
                        >
                          {activeTab === 'pending' && (
                            <td className="px-6 py-5">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(row.txn_id)}
                                className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-emerald-500 focus:ring-emerald-500/30 cursor-pointer"
                              />
                            </td>
                          )}

                          <td className="px-6 py-5">
                            <p className="font-mono text-xs font-black text-zinc-100">
                              #{row.txn_id}
                            </p>
                            <p className="text-[10px] font-bold text-zinc-500 mt-0.5">
                              {row.createdAt}
                            </p>
                          </td>

                          <td className="px-6 py-5">
                            <p className="text-xs font-bold text-zinc-200 truncate max-w-[180px]">
                              {row.name || '—'}
                            </p>
                            <p className="text-[10px] text-zinc-500 truncate max-w-[180px]">
                              {row.email || '—'}
                            </p>
                            <p className="text-[10px] text-zinc-500">
                              {row.number || '—'}
                            </p>
                          </td>

                          <td className="px-6 py-5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-zinc-200 truncate max-w-[140px]">
                                {row.payout_address}
                              </span>
                              <button
                                onClick={() => handleCopy(row.payout_address, row.txn_id)}
                                className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors micro-interaction"
                                aria-label="Copy payout address"
                              >
                                {copiedId === row.txn_id ? (
                                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </td>

                          <td className="px-6 py-5 text-right">
                            <span className="text-base font-black text-emerald-400">
                              ₹{Number(row.amount || 0).toLocaleString()}
                            </span>
                          </td>

                          <td className="px-6 py-5 text-center">
                            {String(row.status).toLowerCase() === 'approved' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-black uppercase">
                                <CheckCircle2 className="w-3 h-3" /> Approved
                              </span>
                            )}
                            {String(row.status).toLowerCase() === 'rejected' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-lg text-[10px] font-black uppercase">
                                <XCircle className="w-3 h-3" /> Rejected
                              </span>
                            )}
                            {String(row.status).toLowerCase() === 'pending' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-[10px] font-black uppercase animate-pulse">
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </td>

                          {activeTab === 'pending' && (
                            <td className="px-6 py-5 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleSingleAction(row.txn_id, 'reject')}
                                  disabled={actionLoading}
                                  aria-label="Reject"
                                  className="p-2 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-xl hover:bg-rose-500 hover:text-white transition-all micro-interaction disabled:opacity-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleSingleAction(row.txn_id, 'approve')}
                                  disabled={actionLoading}
                                  aria-label="Approve"
                                  className="p-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl hover:bg-emerald-500 hover:text-zinc-950 transition-all micro-interaction disabled:opacity-50"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filteredRequests.length > 0 && (
              <div className="p-6 bg-zinc-950/40 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">
                  Showing {(currentPage - 1) * ROWS_PER_PAGE + 1} to{' '}
                  {Math.min(currentPage * ROWS_PER_PAGE, filteredRequests.length)} of{' '}
                  {filteredRequests.length} results
                </p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all micro-interaction"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const p = i + 1;
                      if (
                        totalPages > 7 &&
                        p !== 1 &&
                        p !== totalPages &&
                        Math.abs(p - currentPage) > 1
                      ) {
                        if (p === 2 || p === totalPages - 1) {
                          return (
                            <span key={p} className="px-2 py-2 text-xs font-black text-zinc-600">
                              …
                            </span>
                          );
                        }
                        return null;
                      }
                      return (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`px-3.5 py-2 rounded-xl text-xs font-black transition-all ${
                            currentPage === p
                              ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                              : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all micro-interaction"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminWithdraws;